import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Users,
  Building2,
  Award,
  Briefcase,
  ChevronRight,
  Target,
  Shield,
  Gem,
  Lock,
  FolderArchive,
} from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css";
import IntlMessages from "../../intl/IntlMessages";

const AboutPage = () => {
  const [animatedStats, setAnimatedStats] = useState({
    clients: 0,
    projets: 0,
    experience: 0,
    satisfaction: 0,
  });

  useEffect(() => {
    const targetStats = {
      clients: 12,
      projets: 15,
      experience: 3,
      satisfaction: 98,
    };

    const duration = 2000;
    const startTime = Date.now();

    const animateStats = () => {
      const currentTime = Date.now();
      const progress = Math.min((currentTime - startTime) / duration, 1);

      setAnimatedStats({
        clients: Math.floor(progress * targetStats.clients),
        projets: Math.floor(progress * targetStats.projets),
        experience: Math.floor(progress * targetStats.experience),
        satisfaction: Math.floor(progress * targetStats.satisfaction),
      });

      if (progress < 1) {
        requestAnimationFrame(animateStats);
      } else {
        setAnimatedStats(targetStats);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          requestAnimationFrame(animateStats);
        }
      },
      { threshold: 0.1 }
    );

    const statsSection = document.getElementById("stats-section");
    if (statsSection) {
      observer.observe(statsSection);
    }

    return () => {
      if (statsSection) {
        observer.unobserve(statsSection);
      }
    };
  }, []);

  const customStyles = {
    heroSection: {
      background: "linear-gradient(to right, #0D7C66, #41B3A2)",
      minHeight: "60vh",
      position: "relative",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.2)",
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    primaryColor: {
      color: "#0D7C66",
    },
    secondaryColor: {
      color: "#41B3A2",
    },
    statsSection: {
      backgroundColor: "#0D7C66",
      color: "white",
    },
  };

  return (
    <div>
      {/* Hero Section */}
      <motion.div
        className="position-relative d-flex align-items-center justify-content-center"
        style={customStyles.heroSection}
      >
        <div style={customStyles.overlay}></div>
        <div className="position-relative text-center text-white p-4 z-index-1">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="display-4 fw-bold mb-4"
          >
            <IntlMessages id="about.title" />
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lead mx-auto"
            style={{ maxWidth: "700px" }}
          >
            <IntlMessages id="about.description" />
          </motion.p>
        </div>
      </motion.div>

      {/* Notre Histoire */}
      <div className="container py-5">
        <div className="row align-items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="col-md-6"
          >
            <h2 className="mb-4" style={customStyles.primaryColor}>
              <IntlMessages id="about.history" />
            </h2>
            <p className="text-muted mb-4">
              <IntlMessages id="about.historyDescription" />
            </p>
            <p className="text-muted">
              <IntlMessages id="about.vision" />
            </p>
          </motion.div>
          <div className="col-md-6">
            <div className="row g-4">
              {[
                {
                  icon: Building2,
                  title: <IntlMessages id="values.innovation" />,
                },
                {
                  icon: Target,
                  title: <IntlMessages id="values.excellence" />,
                },
                { icon: Shield, title: <IntlMessages id="values.trust" /> },
                { icon: Gem, title: <IntlMessages id="values.quality" /> },
                { icon: Lock, title: <IntlMessages id="values.security" /> },
                {
                  icon: FolderArchive,
                  title: <IntlMessages id="values.confidentiality" />,
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="col-6"
                >
                  <div className="bg-light p-4 rounded text-center h-100">
                    <item.icon
                      size={40}
                      className="mb-3"
                      style={customStyles.secondaryColor}
                    />
                    <h5 className="fs-6 fw-bold">{item.title}</h5>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div
        id="stats-section"
        className="py-5"
        style={customStyles.statsSection}
      >
        <div className="container">
          <div className="row text-center">
            {[
              {
                icon: Users,
                value: animatedStats.clients,
                label: <IntlMessages id="stats.satisfiedClients" />,
              },
              {
                icon: Briefcase,
                value: animatedStats.projets,
                label: <IntlMessages id="stats.completedProjects" />,
              },
              {
                icon: Award,
                value: animatedStats.experience,
                label: <IntlMessages id="stats.yearsExperience" />,
              },
              {
                icon: Building2,
                value: animatedStats.satisfaction,
                label: <IntlMessages id="stats.satisfactionRate" />,
              },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="col-md-3 mb-4 mb-md-0"
              >
                <stat.icon size={48} className="mb-3" />
                <div className="display-6 fw-bold mb-2">
                  {index === 0
                    ? animatedStats.clients
                    : index === 1
                    ? animatedStats.projets
                    : index === 2
                    ? animatedStats.experience
                    : animatedStats.satisfaction}
                </div>
                <div className="text-uppercase small">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="container py-5">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-5"
          style={customStyles.primaryColor}
        >
          <IntlMessages id="expertises" />
        </motion.h2>
        <div className="row">
          {[
            {
              title: <IntlMessages id="Immobilier_title" />,
              description: <IntlMessages id="Immobilier_description" />,
              features: [
                <IntlMessages id="solutions.propertySales" />,
                <IntlMessages id="solutions.propertyRentals" />,
                <IntlMessages id="solutions.propertyManagement" />,
                <IntlMessages id="solutions.appraisalValuation" />,
                <IntlMessages id="solutions.investmentConsulting" />,
                <IntlMessages id="solutions.newProjectsMarketing" />,
                <IntlMessages id="solutions.legalAssistance" />,
                <IntlMessages id="solutions.condominiumManagement" />,
                <IntlMessages id="solutions.propertyDevelopment" />,
                <IntlMessages id="solutions.mortgageConsulting" />,
                <IntlMessages id="solutions.buyerSellerSupport" />,
                <IntlMessages id="solutions.marketStudies" />,
                <IntlMessages id="solutions.relocationAssistance" />,
                <IntlMessages id="solutions.luxuryManagement" />,
                <IntlMessages id="solutions.wealthConsulting" />,
                <IntlMessages id="content.others" />,
              ],
            },
            {
              title: <IntlMessages id="Événementiel_title" />,
              description: <IntlMessages id="Événementiel_description" />,
              features: [
                <IntlMessages id="content.event1" />,
                <IntlMessages id="content.event2" />,
                <IntlMessages id="content.event3" />,
                <IntlMessages id="content.event4" />,
                <IntlMessages id="content.event5" />,
                <IntlMessages id="content.event6" />,
                <IntlMessages id="content.event7" />,
                <IntlMessages id="content.event8" />,
                <IntlMessages id="content.event9" />,
                <IntlMessages id="content.event10" />,
                <IntlMessages id="content.event11" />,
                <IntlMessages id="content.event12" />,
                <IntlMessages id="content.event13" />,
                <IntlMessages id="content.event14" />,
                <IntlMessages id="content.event15" />,
                <IntlMessages id="content.others" />,
              ],
            },
            {
              title: <IntlMessages id="Services_Informatiques_title" />,
              description: (
                <IntlMessages id="Services_Informatiques_description" />
              ),
              features: [
                <IntlMessages id="content.software1" />,
                <IntlMessages id="content.software2" />,
                <IntlMessages id="content.software3" />,
                <IntlMessages id="content.software4" />,
                <IntlMessages id="content.software5" />,
                <IntlMessages id="content.it1" />,
                <IntlMessages id="content.it2" />,
                <IntlMessages id="content.it3" />,
                <IntlMessages id="content.it4" />,
                <IntlMessages id="content.it5" />,
                <IntlMessages id="content.av1" />,
                <IntlMessages id="content.av2" />,
                <IntlMessages id="content.av3" />,
                <IntlMessages id="content.av4" />,
                <IntlMessages id="content.av5" />,
                <IntlMessages id="content.design1" />,
                <IntlMessages id="content.design2" />,
                <IntlMessages id="content.design3" />,
                <IntlMessages id="content.design4" />,
                <IntlMessages id="content.design5" />,
                <IntlMessages id="content.network1" />,
                <IntlMessages id="content.network2" />,
                <IntlMessages id="content.network3" />,
                <IntlMessages id="content.network4" />,
                <IntlMessages id="content.network5" />,
              ],
            },
            {
              title: <IntlMessages id="content.socialPlatform" />,
              description: <IntlMessages id="content.socialChatDescription" />,
              features: [
                <IntlMessages id="content.socialPlatform1" />,
                <IntlMessages id="content.socialPlatform2" />,
                <IntlMessages id="content.socialPlatform3" />,
                <IntlMessages id="content.socialPlatform4" />,
              ],
            },
            {
              title: <IntlMessages id="content.btp" />,
              description: <IntlMessages id="content.civilDescription" />,
              features: [
                <IntlMessages id="content.civil1" />,
                <IntlMessages id="content.civil2" />,
                <IntlMessages id="content.civil3" />,
                <IntlMessages id="content.civil4" />,
                <IntlMessages id="content.civil5" />,
                <IntlMessages id="content.civil6" />,
                <IntlMessages id="content.civil7" />,
                <IntlMessages id="content.civil8" />,
                <IntlMessages id="content.civil9" />,
                <IntlMessages id="content.civil10" />,
              ],
            },
          ].map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              className="col-md-4 mb-4"
            >
              <div className="card h-100 shadow-sm">
                <div className="card-body">
                  <h3
                    className="card-title h5 mb-3"
                    style={customStyles.primaryColor}
                  >
                    {service.title}
                  </h3>
                  <p className="card-text text-muted mb-4">
                    {service.description}
                  </p>
                  <ul className="list-unstyled">
                    {service.features.map((feature, fIndex) => (
                      <li
                        key={fIndex}
                        className="mb-2 d-flex align-items-center"
                      >
                        <ChevronRight
                          size={16}
                          className="me-2"
                          style={customStyles.secondaryColor}
                        />
                        <span className="text-muted">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
